import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../../../api/client';
import { Tv, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import Login from '../components/Login';

type Status = 'idle' | 'validating' | 'authorizing' | 'success' | 'error';

interface CodeStatusResponse {
  valid: boolean;
  expiresIn?: number;
  status?: string;
}

/**
 * TV Authorization Page
 * Handles device code flow for tvOS authentication
 *
 * Flow:
 * 1. User scans QR or visits /tv?code=XXXX-XXXX
 * 2. If not logged in, show login form
 * 3. If logged in, show authorize button
 * 4. On authorize, TV receives JWT token
 */
const TVAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  const [code, setCode] = useState(searchParams.get('code') || '');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const [expiresIn, setExpiresIn] = useState<number | null>(null);

  // Format code input as XXXX-XXXX
  const formatCode = (value: string): string => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCode(e.target.value);
    setCode(formatted);
    setCodeValid(null);
    setError('');
  };

  // Validate code when entered
  useEffect(() => {
    const validateCode = async () => {
      if (code.length !== 9) { // XXXX-XXXX = 9 chars
        setCodeValid(null);
        return;
      }

      setStatus('validating');
      try {
        const response = await api.get(`/auth/tv/status/${code}`) as CodeStatusResponse;
        setCodeValid(response.valid);
        setExpiresIn(response.expiresIn ?? null);
        if (!response.valid) {
          setError(response.status === 'expired' ? 'Code has expired' : 'Invalid code');
        }
      } catch (err) {
        setCodeValid(false);
        setError('Failed to validate code');
      } finally {
        setStatus('idle');
      }
    };

    const debounce = setTimeout(validateCode, 300);
    return () => clearTimeout(debounce);
  }, [code]);

  // Auto-authorize if user came from QR code and is already logged in
  useEffect(() => {
    const autoAuthorize = async () => {
      const codeFromUrl = searchParams.get('code');
      if (codeFromUrl && currentUser && codeValid === true && status === 'idle') {
        await handleAuthorize();
      }
    };

    if (!authLoading && currentUser && codeValid === true) {
      autoAuthorize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, authLoading, codeValid]);

  const handleAuthorize = async () => {
    if (!code || code.length !== 9) {
      setError('Please enter a valid code');
      return;
    }

    setStatus('authorizing');
    setError('');

    try {
      await api.post('/auth/tv/authorize', { userCode: code });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      const apiError = err as { message?: string };
      setError(apiError.message || 'Failed to authorize TV');
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to app
        </button>

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-700">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Tv className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Connect Your TV</h1>
            <p className="text-gray-400 mt-2">
              Enter the code shown on your Apple TV
            </p>
          </div>

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">TV Connected!</h2>
              <p className="text-gray-400 mb-6">
                Your Apple TV is now signed in. You can close this page.
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Go to App
              </button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Connection Failed</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => {
                  setStatus('idle');
                  setError('');
                  setCode('');
                  setCodeValid(null);
                }}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Normal Flow */}
          {status !== 'success' && status !== 'error' && (
            <>
              {/* Code Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  TV Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="XXXX-XXXX"
                  maxLength={9}
                  className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white text-center text-2xl tracking-widest font-mono placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors ${
                    codeValid === true
                      ? 'border-green-500 focus:ring-green-500'
                      : codeValid === false
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-600 focus:ring-blue-500'
                  }`}
                />
                {codeValid === true && expiresIn && (
                  <p className="text-green-400 text-sm mt-2 text-center">
                    Code valid - expires in {Math.floor(expiresIn / 60)}:{String(expiresIn % 60).padStart(2, '0')}
                  </p>
                )}
                {error && (
                  <p className="text-red-400 text-sm mt-2 text-center">{error}</p>
                )}
              </div>

              {/* Show login if not authenticated */}
              {!currentUser && codeValid === true && (
                <div className="mb-6">
                  <p className="text-gray-400 text-sm text-center mb-4">
                    Sign in to connect your TV
                  </p>
                  <Login onClose={() => {}} />
                </div>
              )}

              {/* Authorize button if authenticated */}
              {currentUser && codeValid === true && (
                <div className="space-y-4">
                  <p className="text-gray-400 text-sm text-center">
                    Signed in as <span className="text-white">{currentUser.email}</span>
                  </p>
                  <button
                    onClick={handleAuthorize}
                    disabled={status === 'authorizing'}
                    className="w-full py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {status === 'authorizing' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Tv className="w-5 h-5" />
                        Connect TV
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Validating state */}
              {status === 'validating' && (
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking code...
                </div>
              )}
            </>
          )}
        </div>

        {/* Help text */}
        <p className="text-gray-500 text-sm text-center mt-6">
          Open Backtime on your Apple TV to get a code
        </p>
      </div>
    </div>
  );
};

export default TVAuthPage;
